/**
 * @component layout-component-picker-modal
 * @css layout-component-picker-modal.css
 * @primitives .btn.btn-secondary, .card
 */
import { escapeHtml } from '../utils/html-utils.js';
import { appendReturnTo } from '../utils/return-to-url.js';
import { catalogEntryDisplayName } from '../utils/layout-component-catalog.js';

class LayoutComponentPickerModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this._title = 'Choose a component';
    this._mode = 'add';
    this._catalog = [];
    this._canAdd = () => true;
    this._currentKey = null;
    this._keydownHandler = this._keydownHandler.bind(this);
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  open(options = {}) {
    this._title = options.title || 'Choose a component';
    this._mode = options.mode === 'change' ? 'change' : 'add';
    this._catalog = Array.isArray(options.catalog) ? options.catalog : [];
    this._canAdd = typeof options.canAdd === 'function' ? options.canAdd : () => true;
    this._currentKey = options.currentKey ?? null;
    this.isOpen = true;
    document.body.style.overflow = 'hidden';
    document.removeEventListener('keydown', this._keydownHandler);
    this.render();
    this.attachEventListeners();
    document.addEventListener('keydown', this._keydownHandler);

    requestAnimationFrame(() => {
      const first = this.querySelector('.layout-component-picker-card:not([disabled])');
      first?.focus();
    });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._keydownHandler);
    this.render();
    this.attachEventListeners();
    this.dispatchEvent(new CustomEvent('component-picker-close', { bubbles: true }));
  }

  _keydownHandler(event) {
    if (this.isOpen && event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  _visibleEntries() {
    if (this._mode === 'change') {
      return this._catalog.filter((entry) => this._canAdd(entry));
    }
    return this._catalog.filter((entry) => this._canAdd(entry));
  }

  _renderCard(entry) {
    const isCurrent = entry.key === this._currentKey;
    const statusBadge = entry.systemCatalogStatus && entry.systemCatalogStatus !== 'none'
      && entry.systemCatalogStatus !== 'approved'
      ? `<span class="layout-component-picker-status">${escapeHtml(entry.systemCatalogStatus)}</span>`
      : '';
    const description = entry.description
      ? `<p class="layout-component-picker-card-desc">${escapeHtml(entry.description)}</p>`
      : '';

    return `
      <button
        type="button"
        class="layout-component-picker-card card${isCurrent ? ' is-current' : ''}"
        data-catalog-key="${escapeHtml(entry.key)}"
        aria-pressed="${isCurrent}"
        ${isCurrent ? 'aria-label="' + escapeHtml(catalogEntryDisplayName(entry)) + ' (current)"' : ''}
      >
        <span class="layout-component-picker-card-icon" aria-hidden="true">${escapeHtml(entry.icon || '🧩')}</span>
        <span class="layout-component-picker-card-body">
          <span class="layout-component-picker-card-name">
            ${escapeHtml(catalogEntryDisplayName(entry))}
            ${statusBadge}
          </span>
          ${description}
        </span>
      </button>
    `;
  }

  _renderBody() {
    const entries = this._visibleEntries();

    if (!this._catalog.length) {
      return `
        <div class="layout-component-picker-empty">
          <p class="form-text">No components are available for this page.</p>
        </div>
      `;
    }

    if (!entries.length) {
      return `
        <div class="layout-component-picker-empty">
          <p class="form-text">All available components are already on this layout.</p>
        </div>
      `;
    }

    const hint = this._mode === 'change'
      ? 'Pick a different component for this region.'
      : 'Pick a component to fill this empty region.';

    const widgetHint = !this._catalog.some((e) => e.catalogGroup === 'my' || e.kind === 'widget')
      ? `<p class="form-text layout-component-picker-widget-hint">Want more? <a href="${appendReturnTo('/settings/widgets')}">Create widgets in Settings</a>.</p>`
      : '';

    const myEntries = entries.filter((e) => e.catalogGroup === 'my');
    const communityEntries = entries.filter((e) => e.catalogGroup !== 'my');

    const renderGroup = (title, groupEntries) => {
      if (!groupEntries.length) return '';
      return `
        <div class="layout-component-picker-group">
          <h4 class="layout-component-picker-group-title">${escapeHtml(title)}</h4>
          <div class="layout-component-picker-grid" role="listbox" aria-label="${escapeHtml(title)}">
            ${groupEntries.map((entry) => this._renderCard(entry)).join('')}
          </div>
        </div>
      `;
    };

    return `
      <p class="modal-description">${hint}</p>
      ${widgetHint}
      ${renderGroup('My widgets', myEntries)}
      ${renderGroup('Community widgets', communityEntries)}
    `;
  }

  render() {
    this.innerHTML = `
      <div class="layout-component-picker-modal ${this.isOpen ? 'open' : ''}" id="layoutComponentPickerModal">
        <div class="modal-backdrop" id="modalBackdrop"></div>
        <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="layoutComponentPickerTitle">
          <div class="modal-header">
            <h3 id="layoutComponentPickerTitle">${escapeHtml(this._title)}</h3>
            <button type="button" class="close-btn" id="closeBtn" aria-label="Close">×</button>
          </div>
          <div class="modal-body">
            ${this.isOpen ? this._renderBody() : ''}
          </div>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    this.querySelector('#modalBackdrop')?.addEventListener('click', () => this.close());
    this.querySelector('#closeBtn')?.addEventListener('click', () => this.close());

    this.querySelectorAll('.layout-component-picker-card').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.catalogKey;
        const entry = this._catalog.find((e) => e.key === key);
        if (!entry || entry.key === this._currentKey) {
          this.close();
          return;
        }
        this.dispatchEvent(new CustomEvent('component-pick', {
          bubbles: true,
          detail: { entry }
        }));
        this.close();
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._keydownHandler);
    if (this.isOpen) {
      document.body.style.overflow = '';
    }
  }
}

customElements.define('layout-component-picker-modal', LayoutComponentPickerModal);

export { LayoutComponentPickerModal };
